/**
 * resolve-targets — on cau un gest de la graella (runbook 7, F3). Funcions pures, amb tests.
 *
 * La graella és una quadrícula exacta: fila d'alçada fixa, columna `dia × cellWidth` (les cards hi
 * van a mig dia, D4: `row-layout.ts` → `calculateReservationPosition`). Per això el
 * destí es decideix **pel punter** (decisió 8), no pel centre del bloc com fa Beds24.
 * Totes les dates són `YYYY-MM-DD` (les de la BD, mitjanit UTC, es tallen als 10 primers caràcters).
 */

/** El mínim d'una reserva per saber si en trepitja una altra. */
export interface StayRange {
  id: string;
  checkIn: string;
  checkOut: string;
  status?: string | null;
}

/** `2027-01-13T00:00:00.000Z` → `2027-01-13`. */
export function toDay(value: string): string {
  return value.slice(0, 10);
}

/** Dies entre dues dates `YYYY-MM-DD` (UTC, sense sorpreses de canvi d'hora). */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${toDay(from)}T00:00:00Z`);
  const b = Date.parse(`${toDay(to)}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

export function addDays(day: string, days: number): string {
  const d = new Date(`${toDay(day)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * La fila sota el punter. `pointerY` es mesura des de dalt de la primera fila (ja inclou el
 * scroll). La frontera entre dues files és de la de sota. Fora de la graella → `null` (cancel·la).
 */
export function resolveRowTarget<Row>(
  pointerY: number,
  rows: readonly Row[],
  cellHeight: number,
): Row | null {
  if (!(cellHeight > 0) || !Number.isFinite(pointerY) || pointerY < 0) return null;
  const index = Math.floor(pointerY / cellHeight);
  return index < rows.length ? rows[index] : null;
}

/** Una fila d'habitació mesurada: `top` i `height` des de dalt de la primera fila. */
export interface RowBox {
  id: string;
  top: number;
  height: number;
}

/**
 * La fila sota el punter quan les files no són totes iguals (mode «Totes» del calendari: entre
 * propietats hi ha una fila separadora amb el nom, que no és cap destí). `boxes` són només les
 * files d'habitació, mesurades; un punter sobre un separador o fora de la graella → `null`.
 */
export function resolveRowTargetInLayout<Row extends { id: string }>(
  pointerY: number,
  boxes: readonly RowBox[],
  roomsById: ReadonlyMap<string, Row>,
): Row | null {
  if (!Number.isFinite(pointerY)) return null;
  for (const box of boxes) {
    if (pointerY >= box.top && pointerY < box.top + box.height) return roomsById.get(box.id) ?? null;
  }
  return null;
}

/**
 * Moure entre propietats no es fa des del PMS (es fa a Beds24, §2.5): si les files són de grups
 * diferents, el destí no val. Sense grups (una sola propietat), tot val.
 */
export function isCrossGroupMove(
  fromUnitId: string,
  toUnitId: string,
  groupOf: ReadonlyMap<string, string> | undefined,
): boolean {
  if (!groupOf) return false;
  return groupOf.get(fromUnitId) !== groupOf.get(toUnitId);
}

/**
 * Nova sortida en estirar la vora dreta. `pointerX` es mesura des de l'esquerra de la primera
 * columna de dies (`gridStart`); la sortida és la frontera de dia més propera al punter, i com a
 * mínim una nit després de l'entrada.
 *
 * `offsetX` és on cau la frontera dins de la columna: `0` = a l'inici del dia; la graella hi passa
 * `cellWidth / 2` perquè les sortides són a mig dia (D4, F6): la sortida del dia `d` és a
 * `d × cellWidth + cellWidth / 2`.
 */
export function resolveResizeTarget(
  pointerX: number,
  reservation: Pick<StayRange, 'checkIn'>,
  cellWidth: number,
  gridStart: string,
  offsetX = 0,
): { checkOut: string; nights: number } {
  const checkIn = toDay(reservation.checkIn);
  const x = pointerX - offsetX;
  const boundary = cellWidth > 0 && Number.isFinite(x) ? Math.round(x / cellWidth) : 0;
  const candidate = addDays(gridStart, boundary);
  const checkOut = candidate > checkIn ? candidate : addDays(checkIn, 1);
  return { checkOut, nights: daysBetween(checkIn, checkOut) };
}

/**
 * La reserva de la unitat de destí que xoca amb `[checkIn, checkOut)`, o `null`. La pròpia
 * (`selfId`) i les cancel·lades no compten.
 */
export function findCollision<T extends StayRange>(
  reservationsOfTargetUnit: readonly T[],
  checkIn: string,
  checkOut: string,
  selfId: string | null,
): T | null {
  const from = toDay(checkIn);
  const to = toDay(checkOut);
  if (to <= from) return null;
  for (const r of reservationsOfTargetUnit) {
    if (r.id === selfId) continue;
    if ((r.status ?? '').toLowerCase() === 'cancelled') continue;
    if (toDay(r.checkIn) < to && from < toDay(r.checkOut)) return r;
  }
  return null;
}

/** Preu proposat en allargar o escurçar: `preu / nits × nits noves`, a 2 decimals. */
export function proposePrice(
  price: number | null | undefined,
  nights: number,
  newNights: number,
): number | null {
  if (price == null || !Number.isFinite(price) || nights < 1 || newNights < 1) return null;
  return Math.round(((price / nights) * newNights) * 100) / 100;
}
